import React, { useState, useRef, useEffect } from "react";
import { FaBold, FaItalic, FaListUl, FaListOl } from "react-icons/fa";

// Simple Rich Text Editor component using contentEditable (NO external libraries)
export const SimpleRichEditor = ({ initialValue = "", onChange, placeholder }) => {
  const editorRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  // Add state to track active formats
  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    bulletList: false,
    numberedList: false
  });

  // Initialize editor content when component mounts or initialValue changes
  useEffect(() => {
    if (editorRef.current) {
      // Only update if the content is different to avoid selection issues
      if (editorRef.current.innerHTML !== initialValue) {
        editorRef.current.innerHTML = initialValue || "<p><br></p>";
      }
    }
  }, [initialValue]);
  
  // Ensure we have a proper paragraph structure on mount
  useEffect(() => {
    if (editorRef.current) {
      if (!editorRef.current.innerHTML || editorRef.current.innerHTML === "<br>") {
        editorRef.current.innerHTML = "<p><br></p>";
      }
      
      // Make sure there's a paragraph inside for proper editing
      if (!editorRef.current.querySelector('p') && !editorRef.current.querySelector('div') &&
          !editorRef.current.querySelector('ul') && !editorRef.current.querySelector('ol')) {
        const content = editorRef.current.innerHTML;
        editorRef.current.innerHTML = `<p>${content}</p>`;
      }
    }
  }, []);

  // Handle changes to the editor content
  const handleInput = () => {
    if (onChange && editorRef.current) {
      const content = editorRef.current.innerHTML;
      // Only trigger onChange if content actually changed
      if (content !== initialValue) {
        onChange(content);
      }
    }
  };
  
  // Check active formatting on selection change
  const checkActiveFormats = () => {
    // Don't check if the editor doesn't have focus
    if (!isFocused || !document.activeElement || !editorRef.current.contains(document.activeElement)) {
      return;
    }
    
    setActiveFormats({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      bulletList: document.queryCommandState('insertUnorderedList'),
      numberedList: document.queryCommandState('insertOrderedList')
    });
  };
  
  // Monitor selection changes to update active format indicators
  useEffect(() => {
    const handleSelectionChange = () => {
      checkActiveFormats();
    };
    
    // Update active formats when selection changes
    document.addEventListener('selectionchange', handleSelectionChange);
    
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, [isFocused]);
  
  // Apply formatting when toolbar button is clicked
  const formatText = (command) => {
    // Focus the editor to ensure we can apply formatting
    editorRef.current.focus();
    
    // Apply the appropriate formatting command
    if (command === 'bold' || command === 'italic') {
      document.execCommand(command, false, null);
    } else if (command === 'bulletList') {
      document.execCommand('insertUnorderedList', false, null);
    } else if (command === 'numberedList') {
      document.execCommand('insertOrderedList', false, null);
    } else if (command === 'clear') {
      document.execCommand('removeFormat', false, null);
      
      // Convert lists to paragraphs
      const lists = editorRef.current.querySelectorAll('ul, ol');
      if (lists.length > 0) {
        lists.forEach(list => {
          // Convert each list item to a paragraph
          const items = Array.from(list.querySelectorAll('li'));
          const fragment = document.createDocumentFragment();
          
          items.forEach(item => {
            const p = document.createElement('p');
            p.innerHTML = item.innerHTML || '<br>';
            fragment.appendChild(p);
          });
          
          if (list.parentNode) {
            list.parentNode.replaceChild(fragment, list);
          }
        });
      }
    }
    
    // Update active formats after applying the command
    setTimeout(() => {
      checkActiveFormats();
      handleInput();
    }, 10);
  };
  
  // For keyboard handling
  const handleKeyDown = (e) => {
    // Add special handling for Tab within lists
    if (e.key === 'Tab' && isInList()) {
      e.preventDefault();
      document.execCommand(e.shiftKey ? 'outdent' : 'indent', false, null);
      handleInput();
    }
  };
  
  // Helper to check if cursor is in a list
  const isInList = () => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return false;

    let node = selection.getRangeAt(0).commonAncestorContainer;
    while (node && node !== editorRef.current) {
      if (['UL', 'OL', 'LI'].includes(node.nodeName)) {
        return true;
      }
      node = node.parentNode;
    }
    return false;
  };
  
  // Check active formats on focus
  const handleFocus = () => {
    setIsFocused(true);
    // Wait a bit to check active formats after focus is established
    setTimeout(checkActiveFormats, 10);
  };
  
  return (
    <div className="border rounded-md overflow-hidden rich-editor-container">
      {/* Toolbar with active state indicators */}
      <div className="flex p-1 border-b bg-gray-50">
        <button
          type="button"
          className={`p-1 mx-1 rounded ${activeFormats.bold ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-200'}`}
          title="Bold"
          onMouseDown={(e) => {
            e.preventDefault(); // Prevent losing focus
            formatText('bold');
          }}
        >
          <FaBold />
        </button>
        <button
          type="button"
          className={`p-1 mx-1 rounded ${activeFormats.italic ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-200'}`}
          title="Italic"
          onMouseDown={(e) => {
            e.preventDefault();
            formatText('italic');
          }}
        >
          <FaItalic />
        </button>
        <button
          type="button"
          className={`p-1 mx-1 rounded ${activeFormats.bulletList ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-200'}`}
          title="Bullet List"
          onMouseDown={(e) => {
            e.preventDefault();
            formatText('bulletList');
          }}
        >
          <FaListUl />
        </button>
        <button
          type="button"
          className={`p-1 mx-1 rounded ${activeFormats.numberedList ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-200'}`}
          title="Numbered List"
          onMouseDown={(e) => {
            e.preventDefault();
            formatText('numberedList');
          }}
        >
          <FaListOl />
        </button>
        <button
          type="button"
          className="p-1 mx-1 rounded hover:bg-gray-200 ml-auto"
          title="Clear Formatting"
          onMouseDown={(e) => {
            e.preventDefault();
            formatText('clear');
          }}
        >
          Clear
        </button>
      </div>
      
      {/* Editor with updated event handlers */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        className="p-3 min-h-[100px] rich-text-editor"
        onInput={() => {
          handleInput();
          checkActiveFormats();
        }}
        onFocus={handleFocus}
        onBlur={() => setIsFocused(false)}
        onKeyUp={checkActiveFormats}
        onMouseUp={checkActiveFormats}
        onKeyDown={handleKeyDown}
        data-placeholder={
          !isFocused && 
          (!editorRef.current || !editorRef.current.textContent.trim()) 
            ? placeholder 
            : ""
        }
        onPaste={(e) => {
          // Handle paste to maintain formatting but avoid unwanted elements
          e.preventDefault();
          const text = e.clipboardData.getData('text/plain');
          document.execCommand('insertText', false, text);
          
          // Update active formats after pasting
          setTimeout(checkActiveFormats, 10);
        }}
      />
      
      {/* Styles */}
      <style jsx global>{`
        .rich-editor-container {
          position: relative;
        }
        
        .rich-text-editor {
          outline: none;
          line-height: 1.5;
          font-family: inherit;
        }
        
        .rich-text-editor:empty:before {
          content: attr(data-placeholder);
          color: #aaa;
          pointer-events: none;
          position: absolute;
        }
        
        .rich-text-editor p {
          margin: 0 0 0.5em 0;
        }
        
        .rich-text-editor p:last-child {
          margin-bottom: 0;
        }
        
        .rich-text-editor ul {
          list-style-type: disc !important;
          padding-left: 1.5em !important;
          margin: 0.5em 0 !important;
        }
        
        .rich-text-editor ol {
          list-style-type: decimal !important;
          padding-left: 1.5em !important;
          margin: 0.5em 0 !important;
        }
        
        .rich-text-editor li {
          display: list-item !important;
        }
        
        .rich-text-editor strong, .rich-text-editor b {
          font-weight: bold !important;
        }
        
        .rich-text-editor em, .rich-text-editor i {
          font-style: italic !important;
        }
      `}</style>
    </div>
  );
};

export default SimpleRichEditor;
